import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { cardUnitArs, DEFAULT_MP_FEE_PCT, formatArs, formatUsd, transferUnitArs, type DisplayCurrency } from "../../shared/money";
import { useI18n } from "../i18n/I18nProvider";

export const CURRENCY_STORAGE_KEY = "pokefans.currency";
/** Shoppers can switch to US$ (off for now: the store shows pesos only; dollars only if the rate is unknown). */
export const ALLOW_USD = false;

export interface MoneyLine {
  /** USD cents. */
  unitPrice: number;
  /** Fixed peso (bank-transfer) price set by an admin (wins over the conversion). */
  unitPriceArs?: number | null;
  quantity: number;
}

export interface MoneyValue {
  /** What the shopper chose to see; prices are always stored in USD. */
  currency: DisplayCurrency;
  setCurrency: (c: DisplayCurrency) => void;
  /** Pesos need the dólar blue; without it prices stay in USD. */
  canShowArs: boolean;
  rate: number | null;
  /** Mercado Pago's fee (%) used to turn transfer prices into card prices. */
  mpFeePct: number;
  /** Peso prices are bank-transfer prices (transfers on). */
  transferPrices: boolean;
  /**
   * Formats an amount in USD cents in the chosen currency. Pesos show the bank-transfer price (the
   * main peso price) while transfers are on, else the Mercado Pago / card price.
   */
  format: (usdCents: number, fixedArs?: number | null) => string;
  /** Total of several lines, converting each unit price first; `card` totals at the Mercado Pago price. */
  formatLines: (lines: MoneyLine[], opts?: { card?: boolean }) => string;
  /** Bank-transfer price (pesos) of one unit; null when transfers are off or the rate is unknown. */
  transferPrice: (usdCents: number, fixedArs?: number | null) => string | null;
  /**
   * The smaller price under the main one: in pesos, the Mercado Pago price ("con Mercado Pago"); in
   * dollars, the bank-transfer price in pesos. Null when there is nothing else to show.
   */
  secondaryPrice: (usdCents: number, fixedArs?: number | null) => { kind: "card" | "transfer"; text: string } | null;
}

const MoneyContext = createContext<MoneyValue | null>(null);

function readStored(): DisplayCurrency {
  try {
    return window.localStorage.getItem(CURRENCY_STORAGE_KEY) === "ARS" ? "ARS" : "USD";
  } catch {
    return "USD";
  }
}

/** Mounted by CatalogProvider, which knows the dólar blue from /api/site. */
export function CurrencyProvider({
  rate,
  transferPrices = false,
  mpFeePct = DEFAULT_MP_FEE_PCT,
  children,
}: {
  rate: number | null;
  transferPrices?: boolean;
  mpFeePct?: number;
  children: ReactNode;
}) {
  const { locale } = useI18n();
  const [chosen, setChosen] = useState<DisplayCurrency>(readStored);

  const setCurrency = useCallback((c: DisplayCurrency) => {
    setChosen(c);
    try {
      window.localStorage.setItem(CURRENCY_STORAGE_KEY, c);
    } catch {
      // Storage unavailable: the choice lasts for this visit.
    }
  }, []);

  const value = useMemo<MoneyValue>(() => {
    const currency: DisplayCurrency = rate && (!ALLOW_USD || chosen === "ARS") ? "ARS" : "USD";
    const card = (cents: number, fixed?: number | null) => cardUnitArs(cents, rate ?? 0, fixed, mpFeePct);
    const transfer = (cents: number, fixed?: number | null) => transferUnitArs(cents, rate ?? 0, fixed, mpFeePct);
    // The main peso price: by bank transfer while transfers are on.
    const pesos = transferPrices ? transfer : card;
    const transferText = (cents: number, fixed?: number | null) => (rate && transferPrices && cents > 0 ? formatArs(transfer(cents, fixed), locale) : null);
    return {
      currency,
      setCurrency,
      canShowArs: Boolean(rate),
      rate,
      mpFeePct,
      transferPrices,
      format: (cents, fixed) => (currency === "ARS" ? formatArs(pesos(cents, fixed), locale) : formatUsd(cents, locale)),
      formatLines: (lines, opts) =>
        currency === "ARS"
          ? formatArs(lines.reduce((n, l) => n + (opts?.card ? card : pesos)(l.unitPrice, l.unitPriceArs) * l.quantity, 0), locale)
          : formatUsd(lines.reduce((n, l) => n + l.unitPrice * l.quantity, 0), locale),
      transferPrice: transferText,
      secondaryPrice: (cents, fixed) => {
        if (!rate || !transferPrices || cents <= 0) return null;
        if (currency === "ARS") return { kind: "card", text: formatArs(card(cents, fixed), locale) };
        const t = transferText(cents, fixed);
        return t ? { kind: "transfer", text: t } : null;
      },
    };
  }, [chosen, rate, transferPrices, mpFeePct, locale, setCurrency]);

  return <MoneyContext.Provider value={value}>{children}</MoneyContext.Provider>;
}

/** The same, or null outside the provider (for code that also runs in isolated tests). */
export function useMoneyOptional(): MoneyValue | null {
  return useContext(MoneyContext);
}

export function useMoney(): MoneyValue {
  const ctx = useContext(MoneyContext);
  if (!ctx) throw new Error("useMoney must be used inside <CurrencyProvider> (CatalogProvider mounts it)");
  return ctx;
}
